import * as Sentry from '@sentry/react'

const PHOTO_UPLOAD_FEATURE_TAG = 'photo-upload'
const PHOTO_ACTION_FEATURE_TAG = 'photo-actions'

type PhotoSentryContext = {
  category?: string | null
  patientId?: number | null
  encounterUid?: string | null
  siteId?: string
  capturedPhotosCount?: number
  isRetake?: boolean
  photoId?: number | string | null
  photoFileName?: string | null
}

type PhotoUploadContext = PhotoSentryContext
type PhotoActionContext = PhotoSentryContext

const buildExtra = (context: PhotoSentryContext): Record<string, unknown> => ({
  ...(context.category != null && { category: context.category }),
  ...(context.patientId != null && { patientId: context.patientId }),
  ...(context.encounterUid != null && { encounterUid: context.encounterUid }),
  ...(context.siteId != null && { siteId: context.siteId }),
  ...(context.capturedPhotosCount != null && { capturedPhotosCount: context.capturedPhotosCount }),
  ...(context.isRetake != null && { isRetake: context.isRetake }),
  ...(context.photoId != null && { photoId: context.photoId }),
  ...(context.photoFileName != null && { photoFileName: context.photoFileName }),
})

const addPhotoBreadcrumb = (
  featureTag: string,
  message: string,
  context: PhotoSentryContext = {},
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void => {
  Sentry.addBreadcrumb({
    category: featureTag,
    message,
    level,
    data: {
      ...buildExtra(context),
      ...data,
    },
  })
}

const capturePhotoMessage = (
  featureTag: string,
  message: string,
  context: PhotoSentryContext = {},
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void => {
  Sentry.captureMessage(message, {
    level,
    tags: { feature: featureTag },
    extra: {
      ...buildExtra(context),
      ...data,
    },
  })
}

const capturePhotoError = (
  featureTag: string,
  error: unknown,
  context: PhotoSentryContext = {},
  data?: Record<string, unknown>,
): void => {
  const extra = {
    ...buildExtra(context),
    ...data,
  }

  if (error instanceof Error) {
    Sentry.captureException(error, {
      tags: { feature: featureTag },
      extra,
    })
  } else {
    Sentry.captureMessage('Photo upload error (non-Error thrown)', {
      level: 'error',
      tags: { feature: featureTag },
      extra: {
        ...extra,
        rawError: String(error),
      },
    })
  }
}

export const addPhotoUploadBreadcrumb = (
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void => {
  addPhotoBreadcrumb(PHOTO_UPLOAD_FEATURE_TAG, message, {}, data, level)
}

export const capturePhotoUploadWarning = (
  message: string,
  context: PhotoUploadContext = {},
  data?: Record<string, unknown>,
): void => {
  capturePhotoMessage(PHOTO_UPLOAD_FEATURE_TAG, message, context, data, 'warning')
}

export const capturePhotoUploadError = (
  error: unknown,
  context: PhotoUploadContext = {},
  data?: Record<string, unknown>,
): void => {
  capturePhotoError(PHOTO_UPLOAD_FEATURE_TAG, error, context, data)
}

export const addPhotoActionBreadcrumb = (
  message: string,
  context: PhotoActionContext = {},
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void => {
  addPhotoBreadcrumb(PHOTO_ACTION_FEATURE_TAG, message, context, data, level)
}

export const capturePhotoActionMessage = (
  message: string,
  context: PhotoActionContext = {},
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void => {
  capturePhotoMessage(PHOTO_ACTION_FEATURE_TAG, message, context, data, level)
}

export const capturePhotoActionError = (
  error: unknown,
  context: PhotoActionContext = {},
  data?: Record<string, unknown>,
): void => {
  capturePhotoError(PHOTO_ACTION_FEATURE_TAG, error, context, data)
}
